import { Component, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Parameter, Step } from 'src/app/model/Plan';
import { DeploymentResultExt } from 'src/app/model/SolutionInfo';
import { ActivityService } from 'src/app/service/activity.service';
import { EventService } from 'src/app/service/event.service';
import { PlanCallbackService } from 'src/app/service/plan-callback.service';
import { Events } from 'src/app/service/websocket.service';
import { Utils } from 'src/app/utils';
import { ColumnSort, ColumnType, DataTableColumn, DataTableRow } from '../../controls/datatable/datatable.component';
import { ModalConfig } from '../../controls/modal/modal-config';
import { ModalRef } from '../../controls/modal/modal-ref';
import { Tab, TabColor } from '../../controls/tabs/tabs.component';

enum ActivityDeploymentStepTab {GENERAL, PARAMETERS}

@Component({
    selector: 'app-activity-deployment-step-details',
    templateUrl: './activity-deployment-step-details.component.html',
    styleUrls: ['./activity-deployment-step-details.component.css'],
    standalone: false
})
export class ActivityDeploymentStepDetailsComponent implements OnInit {

  ActivityDeploymentStepTab = ActivityDeploymentStepTab;
  TabColor = TabColor;
  activeTab: ActivityDeploymentStepTab = ActivityDeploymentStepTab.GENERAL;
  generalTab = new Tab('deployment-step-details-general-tab', $localize`General`, undefined, false);
  parameterTab = new Tab('deployment-step-details-parameters-tab', $localize`Parameters`, undefined, false);
  tabs: Tab[] = [this.generalTab, this.parameterTab];
  height:string = '365px';
  step:Step;
  dlgId:string;
  currentDeploymentResult:DeploymentResultExt;
  col: DataTableColumn[] = Array<DataTableColumn>(
    new DataTableColumn("displayName", $localize`Name`, ColumnType.STRING, false, "20", undefined, undefined, undefined, true),
    new DataTableColumn("group", $localize`Group`, ColumnType.STRING, true, "20", ColumnSort.DESC, undefined, undefined, true),
    new DataTableColumn("displayValue", $localize`Value`, ColumnType.STRING, false, "60"));
  row: DataTableRow[] = [];
  stepStatus: any;

  //subscription
  eventMessageSubscription: Subscription;

  constructor(private modalConfig: ModalConfig, private modalRef: ModalRef,
              private eventService: EventService, public activityService: ActivityService) { 
    this.dlgId = this.modalConfig.data.id;
    this.init(this.modalConfig.data.step, this.modalConfig.data.currentDeploymentResult);
  }

  ngOnInit(): void {
    this.sortParameters(this.col[1]); //sort by group

    this.eventMessageSubscription = this.eventService.eventMessage$.subscribe(msg => {
      if(msg.eventType == Events.ActivityRefresh){
        if(this.currentDeploymentResult && this.currentDeploymentResult.executionStatus !== 'COMPLETED' &&
          this.currentDeploymentResult.executionStatus !== 'ERROR'){
            this.activityService.getDeploymentStatus(this.currentDeploymentResult.deploymentId).pipe(
              tap(resp => {
                this.init(resp.body.steps[this.step.stepNo], resp.body);
              }),
              ).subscribe();
          }
      }
    });
  }

  sortParameters(col: DataTableColumn): void {
    this.row = [];
    Object.values(this.step.parameters).filter( p => {
      return p.visible;
    //sort by group then display name
    }).sort((a, b) => {
      if(col.sortDirection == ColumnSort.DESC){
        if(col.field === 'group'){
          return a.group.localeCompare(b.group) || a.displayName.localeCompare(b.displayName);
        }else{
          return a[col.field].localeCompare(b[col.field]);
        }
      }else{
        if(col.field === 'group'){
          return b.group.localeCompare(a.group) || a.displayName.localeCompare(b.displayName);
        }else{
          return b[col.field].localeCompare(a[col.field]);
        }
      }
    }).forEach( param => {
      param.group = param.group == '' ? $localize`Default` : param.group;
      this.row.push(new DataTableRow({displayName: param.displayName, group: param.group, displayValue: this.getParameterValue(param)}, []));
    });
  }

  getParameterValue(param: Parameter): string {
    let retValue:string ='';

    switch(param.type) { 
      case "OBJECT_LIST":
        if(param.objectList){
          let items:Object[] = [];
          param.objectList.forEach( (item) => {
            let obj = new Object();
            Object.keys(item).forEach(key => {
              obj[key] = new Object();
              obj[key] = '';

              if(item[key].type == 'LIST'){
                if(item[key].defaultListValue){
                  for(var i = 0; i < item[key].defaultListValue.length; i++) {
                    if(item[key].defaultListValue[i].selected){
                      obj[key] = item[key].defaultListValue[i].value;
                    }
                  }
                }
              }else if(item[key].type == 'STRING'){
                obj[key] = item[key].value;
              }
            });
            items.push(obj);
          });
          retValue = JSON.stringify(items);
        }
        break; 
      case "DOWNLOAD_FILE_MULTI":
        if(param.valueList){
          param.valueList.sort((a, b) => {
            return a.name.localeCompare(b.name);
          }).forEach( (item) => {
            retValue += item.name + ", ";
          });

          if(retValue.length > 0){
						retValue = retValue.substring(0, retValue.length - 2);
					}
        }
         break; 
      case "LIST":
        if (param.defaultListValue) {
          for(var i = 0; i < param.defaultListValue.length; i++) {
            if(param.value == param.defaultListValue[i].value){
              retValue = param.defaultListValue[i].name;
              break;
            }
          }
        }
        break; 
      default:
        retValue = param.sensitive ? '*****' : param.value;
        break;
   } 

    return retValue;
  }

  checkParameters(step:Step): void {
    let parameters:Parameter[] = Object.values(step.parameters);

    parameters.forEach((param) => {
      if(param.dependencies){
        let isDependencyValidated:boolean = true;

        for(let dep of param.dependencies){
          let p = step.parameters[dep.name];

          //check for callback
          if(dep.callback && typeof PlanCallbackService[dep.callback] === "function"){
            isDependencyValidated = PlanCallbackService[dep.callback](step.parameters, dep, dep.callbackValue);
            if(!isDependencyValidated){
              break;
            }
          }else{
            if(dep.name === p.name && (p.value == '' || p.value !== dep.value)){
              isDependencyValidated = false;
              break;
            }
          }
        }

        param.visible = isDependencyValidated; //only visible if dependency is validated
      }else{
        param.visible = true;
      }
    });
  }
  
  ngOnDestroy(){
    this.eventMessageSubscription.unsubscribe();
  }

  getTitle(): string {
    return $localize`${this.step.nickname} details`;
  }

  close():void {
    this.modalRef.close();
  }

  onSelectedTabChange(selectedIndex: number) {
    this.activeTab = selectedIndex;
  }

  init(step:Step, deploymentResult:DeploymentResultExt):void {
    this.step = step;
    this.checkParameters(this.step);
    this.currentDeploymentResult = deploymentResult;
    this.stepStatus = Utils.getDeploymentStepStatus(this.currentDeploymentResult, this.step.status);
  }
}